import { iconFileUpload, iconWhiteTrash } from "assets/icons";
import { Button } from "components/Button";
import { useGraphQLAuth } from "hooks/useGraphQLAuth";
import { useToast } from "hooks/useToast";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { FileService } from "services/fileService";
import styled from "styled-components";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  label {
    font-size: 14px;
    font-weight: 500;
    color: #333;
    margin-bottom: 0.25rem;
  }
`;

const UploadArea = styled.div<{ error?: boolean }>`
  border: 2px dashed ${(props) => (props.error ? "#e74c3c" : "#ddd")};
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #fafafa;

  &:hover {
    border-color: ${(props) => (props.error ? "#e74c3c" : "#3498db")};
    background-color: #f0f8ff;
  }
`;

const UploadText = styled.div`
  color: #666;
  font-size: 14px;

  small {
    color: #999;
    font-size: 12px;
  }
`;

const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  background-color: #fafafa;
`;

const PreviewImage = styled.img`
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 50%;
  border: 3px solid #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const ErrorText = styled.span`
  color: #e74c3c;
  font-size: 12px;
  margin-top: 0.25rem;
`;

interface ProfilePhotoUploadProps {
  registrationId?: string;
  currentPhotoUrl?: string;
  onPhotoUploaded?: (photoData: any) => void;
  onPhotoRemoved?: () => void;
  label?: string;
  error?: boolean;
  errorMessage?: string;
}

export interface ProfilePhotoUploadRef {
  uploadPhoto: (overrideRegistrationId?: string) => Promise<boolean>;
  hasSelectedPhoto: () => boolean;
}

export const ProfilePhotoUpload = forwardRef<
  ProfilePhotoUploadRef,
  ProfilePhotoUploadProps
>(
  (
    {
      registrationId,
      currentPhotoUrl,
      onPhotoUploaded,
      onPhotoRemoved,
      label = "Foto de Perfil",
      error = false,
      errorMessage,
    },
    ref
  ) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const { setToast } = useToast();
    const { connectGraphQLApi } = useGraphQLAuth();

    // Expose functions to parent component
    useImperativeHandle(ref, () => ({
      uploadPhoto: async (
        overrideRegistrationId?: string
      ): Promise<boolean> => {
        const targetRegistrationId = overrideRegistrationId || registrationId;
        if (!selectedImage || !targetRegistrationId) {
          return false;
        }
        return await uploadImage(targetRegistrationId);
      },
      hasSelectedPhoto: (): boolean => {
        console.log("hasSelectedPhoto called, selectedImage:", selectedImage);
        return selectedImage !== null;
      },
    }));

    // Set initial image preview
    useEffect(() => {
      if (currentPhotoUrl) {
        setImagePreview(currentPhotoUrl);
      }
    }, [currentPhotoUrl]);

    // Cleanup object URLs
    useEffect(() => {
      return () => {
        if (imagePreview && imagePreview.startsWith("blob:")) {
          URL.revokeObjectURL(imagePreview);
        }
      };
    }, [imagePreview]);

    const handleImageClick = () => {
      fileInputRef.current?.click();
    };

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        console.log(file.type);
        // Validate file type
        if (!file.type.startsWith("image/")) {
          setToast({
            type: "error",
            message: "Por favor, selecione apenas arquivos de imagem.",
            isActive: true,
            timeToRemoveToast: 3000,
          });
          return;
        }

        // Validate file size (15MB limit)
        if (file.size > 15 * 1024 * 1024) {
          setToast({
            type: "error",
            message: "A imagem é muito grande. O tamanho máximo é 15MB.",
            isActive: true,
            timeToRemoveToast: 3000,
          });
          return;
        }

        setSelectedImage(file);

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    };

    const uploadImage = async (
      targetRegistrationId?: string
    ): Promise<boolean> => {
      const regId = targetRegistrationId || registrationId;
      if (!selectedImage || !regId) return false;

      setIsUploading(true);

      try {
        const res = await FileService.uploadFile({
          file: selectedImage,
          refCollectionId: regId,
          refCollectionName: "registrations",
          refCollectionField: "profile.photoId",
          refCollectionFieldType: "string",
        });
        setToast({
          type: "success",
          message: "Imagem carregada com sucesso!",
          isActive: true,
          timeToRemoveToast: 3000,
        });

        onPhotoUploaded?.(res.fileUrl);
        setSelectedImage(null);
        return true;
      } catch (error: any) {
        console.error("Error uploading image:", error);
        setToast({
          type: "error",
          message: error.message || "Erro ao fazer upload da imagem.",
          isActive: true,
          timeToRemoveToast: 3000,
        });
        return false;
      } finally {
        setIsUploading(false);
      }
    };

    const removeImage = async () => {
      // If there's a selected image but not uploaded yet, just clear it
      if (selectedImage && !currentPhotoUrl) {
        setSelectedImage(null);
        setImagePreview(null);
        return;
      }

      if (!currentPhotoUrl) return;

      try {
        const mutation = `
        mutation BackofficeDeleteFile($fileId: ID!) {
          backofficeDeleteFile(fileId: $fileId)
        }
      `;

        // Extract file ID from URL or use a different approach
        // For now, we'll just clear the image locally and let the parent handle the removal
        setImagePreview(null);
        setSelectedImage(null);
        onPhotoRemoved?.();

        setToast({
          type: "success",
          message: "Imagem removida com sucesso!",
          isActive: true,
          timeToRemoveToast: 3000,
        });
      } catch (error: any) {
        console.error("Error removing image:", error);
        setToast({
          type: "error",
          message: error.message || "Erro ao remover imagem.",
          isActive: true,
          timeToRemoveToast: 3000,
        });
      }
    };

    const resetImage = () => {
      setSelectedImage(null);
      setImagePreview(currentPhotoUrl || null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };

    return (
      <Container>
        <label>{label}</label>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          style={{ display: "none" }}
        />

        {imagePreview ? (
          <PreviewContainer>
            <PreviewImage src={imagePreview} alt="Preview da foto de perfil" />
            <ButtonContainer>
              <Button
                label="Alterar"
                typeBtn="outline"
                size="small"
                icon={iconFileUpload}
                onClick={handleImageClick}
                type="button"
              />
              <Button
                label="Remover"
                typeBtn="danger"
                size="small"
                icon={iconWhiteTrash}
                onClick={removeImage}
                type="button"
              />
              {selectedImage && (
                <Button
                  label="Cancelar Seleção"
                  typeBtn="outline"
                  size="small"
                  onClick={resetImage}
                  type="button"
                />
              )}
            </ButtonContainer>
          </PreviewContainer>
        ) : (
          <UploadArea onClick={handleImageClick} error={error}>
            <UploadText>
              Clique aqui para selecionar uma imagem
              <br />
              <small>Formatos aceitos: JPG, PNG (máx. 15MB)</small>
            </UploadText>
          </UploadArea>
        )}

        {error && errorMessage && <ErrorText>{errorMessage}</ErrorText>}
      </Container>
    );
  }
);
